/**
 * Microcopy — TanStack Query bindings.
 *
 * GET /api/v1/tenants/{id}/microcopy — read tenant slots
 * PUT /api/v1/tenants/{id}/microcopy — write all 5 slots
 *
 * The PUT is intentionally not step-up gated server-side
 * (microcopy changes don't shift auth posture); the mutation
 * still goes through the canonical apiFetch path.
 */

import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import {
  microcopyResponseSchema,
  type MicrocopyFormValues,
  type MicrocopyResponse,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/microcopy`;
}

const microcopyKey = (tenantID: string) => ["microcopy", tenantID] as const;

/** For route loaders: the same key and fetch the hook uses. */
export const microcopyQueryOptions = (tenantID: string) =>
  queryOptions<MicrocopyResponse, ApiError>({
    queryKey: microcopyKey(tenantID),
    queryFn: async () => {
      const raw = await apiFetch<unknown>(base(tenantID));
      return microcopyResponseSchema.parse(raw);
    },
  });

export function useMicrocopyQuery(tenantID: string) {
  return useQuery<MicrocopyResponse, ApiError>({
    ...microcopyQueryOptions(tenantID),
    enabled: tenantID.length > 0,
  });
}

export function useSaveMicrocopyMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<MicrocopyResponse, ApiError, MicrocopyFormValues>({
    mutationFn: async (values) => {
      const raw = await apiFetch<unknown>(base(tenantID), {
        method: "PUT",
        body: values,
      });
      return microcopyResponseSchema.parse(raw);
    },
    onSuccess: (saved) => {
      qc.setQueryData(microcopyKey(tenantID), saved);
    },
  });
}
